import type { components } from "../../api/schema";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import { isPersonaSuppliableDeclaration } from "../../lib/variableCascade";

export type ScopeType = components["schemas"]["ScopeType"];
export type Variable = components["schemas"]["VariableResponse"];
export type VariableCreate = components["schemas"]["VariableCreate"];
export type VariablePatch = components["schemas"]["VariablePatch"];
export type VariableValueType = components["schemas"]["VariableValueType"];
export type ReservedVariableKey =
  components["schemas"]["ReservedVariableKeyResponse"];

export interface VariableFormState {
  allowedValues: string;
  defaultValue: string;
  isRequired: boolean;
  isSensitive: boolean;
  key: string;
  scopeId: string;
  scopeType: ScopeType;
  valueType: VariableValueType;
}

export function initialVariableFormState(
  scopeType: ScopeType = "agent",
  scopeId = "",
  key = "",
): VariableFormState {
  return {
    allowedValues: "",
    defaultValue: "",
    isRequired: false,
    isSensitive: false,
    key,
    scopeId,
    scopeType,
    valueType: "string",
  };
}

export function variableValueText(value: unknown): string {
  if (value === null) {
    return "";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return JSON.stringify(value);
}

export function variableFormState(variable: Variable): VariableFormState {
  const revision = variable.current_revision;
  return {
    allowedValues: revision.is_sensitive
      ? ""
      : (revision.allowed_values ?? [])
          .filter((value): value is string => typeof value === "string")
          .join(", "),
    defaultValue: revision.is_sensitive
      ? ""
      : variableValueText(revision.default_value),
    isRequired: revision.is_required,
    isSensitive: revision.is_sensitive,
    key: variable.key,
    scopeId: variable.scope_id,
    scopeType: variable.scope_type,
    valueType:
      revision.value_type === "string" ||
      revision.value_type === "number" ||
      revision.value_type === "bool" ||
      revision.value_type === "enum"
        ? revision.value_type
        : "string",
  };
}

// The warning reads the declaration as it will exist *after* this save, not as
// it is persisted: clearing a scenario default in the same edit as a rename
// makes the slot persona-suppliable, and adding one makes it not. A masked
// default survives the patch untouched (toVariablePatch omits it), so the
// persisted default is the effective one in that case.
export function personaRenameWarning(
  variable: Variable | null,
  state: VariableFormState,
): string | null {
  if (
    variable === null ||
    unicodeCaseFold(state.key.trim()) === unicodeCaseFold(variable.key)
  ) {
    return null;
  }
  const revision = variable.current_revision;
  const hasDefault =
    revision.is_sensitive && state.isSensitive
      ? revision.default_value !== null
      : state.defaultValue.trim().length > 0;
  const isPersonaSuppliable = isPersonaSuppliableDeclaration(
    {
      hasDefault,
      scopeId: variable.scope_id,
      scopeType: variable.scope_type,
    },
    // A persona-scope declaration is supplied by the persona that owns it.
    variable.scope_id,
  );
  if (!isPersonaSuppliable) {
    return null;
  }
  if (variable.scope_type === "persona") {
    return (
      "Persona-supplied values bind by key name. Renaming this declaration can " +
      "detach existing persona values at launch; unmatched values are dropped " +
      "and may surface as missing or required missing."
    );
  }
  return (
    "This scenario slot can be supplied by any persona by key name. " +
    "Renaming it can detach existing persona values at launch; unmatched " +
    "values are dropped and may surface as missing or required missing."
  );
}

export function parsedAllowedValues(state: VariableFormState): string[] | null {
  if (state.valueType !== "enum") {
    return null;
  }
  return state.allowedValues
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

// `valueType` is widened past the closed enum because a stored revision may
// carry a value type this build does not know: every caller reads it off a
// `VariableRevisionResponse`, whose generated type is `VariableValueType |
// string`. Unknown types fail closed rather than being coerced to a string.
export function parseVariableValue(
  key: string,
  valueType: string,
  allowedValues: readonly string[] | null,
  value: string,
): components["schemas"]["JsonValue"] {
  const raw = value.trim();
  switch (valueType) {
    case "string":
      // The empty string is an explicit string value. Callers that allow a
      // missing value (a nullable declaration default) check for it first.
      return value;
    case "number": {
      const parsed = Number(raw);
      if (raw.length === 0 || !Number.isFinite(parsed)) {
        throw new Error(`${key} must be a number.`);
      }
      if (Number.isInteger(parsed) && !Number.isSafeInteger(parsed)) {
        throw new Error(`${key} must be a safe integer.`);
      }
      return parsed;
    }
    case "bool": {
      // Spreadsheet exports pad and upper-case booleans (docs/09 CSV import).
      const folded = raw.toLowerCase();
      if (folded === "true") return true;
      if (folded === "false") return false;
      throw new Error(`${key} must be true or false.`);
    }
    case "enum": {
      if (allowedValues?.includes(value)) return value;
      throw new Error(
        `${key} must be one of: ${(allowedValues ?? []).join(", ")}.`,
      );
    }
    default:
      throw new Error(
        `${key} uses unsupported value type "${valueType}" and cannot be ` +
          `saved safely.`,
      );
  }
}

function parsedDefaultValue(
  state: VariableFormState,
): components["schemas"]["JsonValue"] {
  // A declaration default is nullable: a blank field means "no default".
  if (state.defaultValue.trim().length === 0) {
    return null;
  }
  return parseVariableValue(
    state.key.trim(),
    state.valueType,
    parsedAllowedValues(state),
    state.defaultValue,
  );
}

export function variableFormError(
  state: VariableFormState,
  preserveMaskedValues = false,
  requiresSensitiveReplacement = false,
  reservedKeys?: ReservedVariableKey[],
): string | null {
  if (reservedKeys === undefined) {
    return "Variable key rules are unavailable. Try again when they load.";
  }
  if (state.key.trim().length === 0 || state.scopeId.length === 0) {
    return "Key and scope are required.";
  }
  const reserved = reservedKeys.find(
    (rule) => unicodeCaseFold(rule.key) === unicodeCaseFold(state.key.trim()),
  );
  if (reserved) {
    return `${state.key.trim()} is reserved as a ${reserved.kind}.`;
  }
  const allowedValues = parsedAllowedValues(state);
  if (
    !preserveMaskedValues &&
    state.valueType === "enum" &&
    allowedValues?.length === 0
  ) {
    return "Enum variables require at least one allowed value.";
  }
  if (!preserveMaskedValues && state.defaultValue.trim().length > 0) {
    try {
      parseVariableValue(
        state.key.trim(),
        state.valueType,
        allowedValues,
        state.defaultValue,
      );
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    }
  }
  if (requiresSensitiveReplacement && state.defaultValue.trim().length === 0) {
    return "A replacement default value is required to remove sensitivity.";
  }
  return null;
}

export function toVariableCreate(state: VariableFormState): VariableCreate {
  return {
    allowed_values: parsedAllowedValues(state),
    default_value: parsedDefaultValue(state),
    is_required: state.isRequired,
    is_sensitive: state.isSensitive,
    key: state.key.trim(),
    scope_id: state.scopeId,
    scope_type: state.scopeType,
    value_type: state.valueType,
  };
}

// Core treats any supplied content field as a content change and mints a new
// revision, so a rename-only save must send `key` alone.
export function toVariablePatch(
  state: VariableFormState,
  variable: Variable,
): Partial<VariablePatch> {
  const revision = variable.current_revision;
  const patch: Partial<VariablePatch> = {
    ...(state.key.trim() !== variable.key ? { key: state.key.trim() } : {}),
    ...(state.valueType !== revision.value_type
      ? { value_type: state.valueType }
      : {}),
    ...(state.isRequired !== revision.is_required
      ? { is_required: state.isRequired }
      : {}),
    ...(state.isSensitive !== revision.is_sensitive
      ? { is_sensitive: state.isSensitive }
      : {}),
  };
  if (revision.is_sensitive && state.isSensitive) {
    // A masked response never gives the browser values it could safely echo.
    // Omitting both fields preserves them through VariablePatch.fields_set.
    return patch;
  }
  const allowedValues = parsedAllowedValues(state);
  const defaultValue = parsedDefaultValue(state);
  return {
    ...patch,
    ...(JSON.stringify(allowedValues) !==
    JSON.stringify(revision.allowed_values)
      ? { allowed_values: allowedValues }
      : {}),
    ...(JSON.stringify(defaultValue) !== JSON.stringify(revision.default_value)
      ? { default_value: defaultValue }
      : {}),
  };
}
